import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Alert, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { usePocketBase } from '../../PocketBaseContext';
import BreadCrumb from "../../Components/Common/BreadCrumb";

const CategoryManagement = () => {
  document.title = "Categories | Reform Panel";

  const pb = usePocketBase();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const categoryTypeOptions = [
    { value: 'Ürün', label: 'Ürün' },
    { value: 'Hizmet', label: 'Hizmet' },
    { value: 'Düzenli Bakim', label: 'Düzenli Bakim' },
  ];

  const fetchCategories = async () => {
    try {
      const records = await pb.collection('category').getFullList(200, {
        sort: '-created',
      });
      setCategories(records);
      setFilteredCategories(records);
    } catch (error) {
      console.error('Kategoriler getirilirken hata oluştu:', error);
      setAlert({ color: 'danger', message: 'Kategoriler getirilirken bir hata oluştu.' });
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const results = categories.filter(category =>
      category.brand_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.category_type.some(type => type.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredCategories(results);
  }, [searchTerm, categories]);

  const toggleEditModal = () => {
    setEditModal(!editModal);
  };

  const toggleAddModal = () => {
    setAddModal(!addModal);
    if (!addModal) {
      addValidation.resetForm();
    }
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
  };

  const handleEdit = (category) => {
    setCurrentCategory(category);
    editValidation.setValues({
      brand_name: category.brand_name,
      category_type: category.category_type,
    });
    toggleEditModal();
  };

  const handleDeleteClick = (category) => {
    setCurrentCategory(category);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    try {
      await pb.collection('category').delete(currentCategory.id);
      await fetchCategories();
      setAlert({ color: 'success', message: 'Kategori başarıyla silindi.' });
      toggleDeleteModal();
    } catch (error) {
      console.error('Kategori silinirken hata oluştu:', error);
      setAlert({ color: 'danger', message: 'Kategori silinirken bir hata oluştu.' });
    }
  };

  const editValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      brand_name: '',
      category_type: [],
    },
    validationSchema: Yup.object({
      brand_name: Yup.string().required('Kategori adı gereklidir'),
      category_type: Yup.array().min(1, 'En az bir kategori türü seçilmelidir'),
    }),
    onSubmit: async (values) => {
      try {
        await pb.collection('category').update(currentCategory.id, values);
        setAlert({ color: 'success', message: 'Kategori başarıyla güncellendi.' });
        toggleEditModal();
        await fetchCategories();
      } catch (error) {
        console.error('Kategori güncellenirken hata oluştu:', error);
        setAlert({ color: 'danger', message: 'Kategori güncellenirken bir hata oluştu: ' + error.message });
      }
    },
  });

  const addValidation = useFormik({
    initialValues: {
      brand_name: '',
      category_type: [],
    },
    validationSchema: Yup.object({
      brand_name: Yup.string().required('Kategori adı gereklidir'),
      category_type: Yup.array().min(1, 'En az bir kategori türü seçilmelidir'),
    }),
    onSubmit: async (values) => {
      try {
        await pb.collection('category').create(values);
        setAlert({ color: 'success', message: 'Kategori başarıyla eklendi.' });
        toggleAddModal();
        await fetchCategories();
        addValidation.resetForm();
      } catch (error) {
        console.error('Kategori eklenirken hata oluştu:', error);
        setAlert({ color: 'danger', message: 'Kategori eklenirken bir hata oluştu: ' + error.message });
      }
    },
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Kategori Yönetimi" pageTitle="Kategoriler" />
          
          {alert && (
            <Alert color={alert.color} className="material-shadow">
              <strong>{alert.message}</strong>
            </Alert>
          )}
          
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">Kategoriler</h4>
                  <Button color="primary" onClick={toggleAddModal}>
                      Yeni Kategori Ekle
                    </Button>
                </CardHeader>
                <CardBody>
                  <Container fluid>
                    <Row className="mb-3">
                      <Col xs="12" sm="6" md="4">
                        <Input
                          type="search"
                          className="form-control"
                          placeholder="Personel ara..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </Col>
                    </Row>
                  </Container>
                </CardBody>
            
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table-borderless align-middle table-nowrap mb-0">
                      <thead>
                        <tr>
                          <th scope="col">ID</th>
                          <th scope="col">Kategori Adı</th>
                          <th scope="col">Kategori Türü</th>
                          <th scope="col">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCategories.map((category) => (
                          <tr key={category.id}>
                            <td className="fw-medium">{category.id}</td>
                            <td>{category.brand_name}</td>
                            <td>{category.category_type.join(', ')}</td>
                            <td>
                              <div className="hstack gap-3 fs-15">
                                <Link to="#" className="link-primary" onClick={() => handleEdit(category)}>
                                  <i className="ri-edit-2-line"></i>
                                </Link>
                                <Link to="#" className="link-danger" onClick={() => handleDeleteClick(category)}>
                                  <i className="ri-delete-bin-5-line"></i>
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Düzenleme Modal */}
      <Modal isOpen={editModal} toggle={toggleEditModal} size="lg">
        <ModalHeader toggle={toggleEditModal}>
          Kategori Düzenle
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={editValidation.handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="brand_name">Kategori Adı</Label>
                  <Input
                    type="text"
                    name="brand_name"
                    id="brand_name"
                    value={editValidation.values.brand_name}
                    onChange={editValidation.handleChange}
                    onBlur={editValidation.handleBlur}
                    invalid={editValidation.touched.brand_name && editValidation.errors.brand_name}
                  />
                  {editValidation.touched.brand_name && editValidation.errors.brand_name && (
                    <div className="text-danger">{editValidation.errors.brand_name}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="category_type">Kategori Türü</Label>
                  <Select
                    isMulti
                    id="category_type"
                    name="category_type"
                    options={categoryTypeOptions}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={categoryTypeOptions.filter(option => editValidation.values.category_type.includes(option.value))}
                    onChange={(selectedOptions) => {
                      editValidation.setFieldValue('category_type', selectedOptions.map(option => option.value));
                    }}
                  />
                  {editValidation.touched.category_type && editValidation.errors.category_type && (
                    <div className="text-danger">{editValidation.errors.category_type}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Button color="primary" type="submit">
              Güncelle
            </Button>
          </Form>
        </ModalBody>
      </Modal>

      {/* Ekleme Modal */}
      <Modal isOpen={addModal} toggle={toggleAddModal} size="lg">
        <ModalHeader toggle={toggleAddModal}>
          Yeni Kategori Ekle
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={addValidation.handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="brand_name">Kategori Adı</Label>
                  <Input
                    type="text"
                    name="brand_name"
                    id="brand_name"
                    value={addValidation.values.brand_name}
                    onChange={addValidation.handleChange}
                    onBlur={addValidation.handleBlur}
                    invalid={addValidation.touched.brand_name && addValidation.errors.brand_name}
                  />
                  {addValidation.touched.brand_name && addValidation.errors.brand_name && (
                    <div className="text-danger">{addValidation.errors.brand_name}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="category_type">Kategori Türü</Label>
                  <Select
                    isMulti
                    id="category_type"
                    name="category_type"
                    options={categoryTypeOptions}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={categoryTypeOptions.filter(option => addValidation.values.category_type.includes(option.value))}
                    onChange={(selectedOptions) => {
                      addValidation.setFieldValue('category_type', selectedOptions.map(option => option.value));
                    }}
                  />
                  {addValidation.touched.category_type && addValidation.errors.category_type && (
                    <div className="text-danger">{addValidation.errors.category_type}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Button color="primary" type="submit">
              Ekle
            </Button>
          </Form>
        </ModalBody>
      </Modal>

      {/* Silme Onay Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Kategori Sil</ModalHeader>
        <ModalBody>
          <p>"{currentCategory?.brand_name}" kategorisini silmek istediğinizden emin misiniz?</p>
          <p>Bu işlem geri alınamaz.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>İptal</Button>
          <Button color="danger" onClick={handleDelete}>Sil</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default CategoryManagement;